import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { db, auth } from './firebase';
import { onIdTokenChanged } from 'firebase/auth';
import { collection, getDocs } from 'firebase/firestore';
import { useLocalStorage } from './hooks/useLocalStorage';
import { getScrollRoot, scrollRootTo } from './hooks/useScrollReset';
import { trackPageView } from './utils/analyticsUtils';
import HomeScreen from './components/home/HomeScreen';
import { topicByLabel, topicNav } from './data/educationTaxonomy';
import { logEvent } from './utils/telemetry';
import { startProfileSync } from './services/profile';
import { startDeviceSync } from './services/deviceStore';
import { useProfile } from './services/profile';
import { startNotifications, startAppBadge } from './services/notifications';
import { refreshPushToken, onForegroundPush } from './services/push';
import { notify } from './utils/notify';
import EducationCollectionScreen from './components/screens/education/EducationCollectionScreen';
import EducationBrowseScreen from './components/screens/education/EducationBrowseScreen';
import HospitalDetailScreen from './components/hospital/HospitalDetailScreen';
import EmergencyProtocolsScreen from './components/screens/EmergencyProtocolsScreen';
import EducationCenterScreen from './components/screens/EducationCenterScreen';
import EducationTopicScreen from './components/screens/education/EducationTopicScreen';
import ConcernsScreen from './components/screens/ConcernsScreen';
import NeedlestickScreen from './components/screens/NeedlestickScreen';
import BCMAnesthesiologyScreen from './components/bcm/BCMAnesthesiologyScreen';
import BCMResourcesScreen from './components/bcm/BCMResourcesScreen';
import ResearchHubScreen from './components/screens/ResearchHubScreen';
import SearchResultsScreen from './components/screens/SearchResultsScreen';
import ClinicalToolsScreen from './components/screens/ClinicalToolsScreen';
import MedicationReferenceScreen from './components/screens/MedicationReferenceScreen';
import PreopMedicationGuide from './components/screens/PreopMedicationGuide';
import ASRAAnticoagulation from './components/screens/ASRAAnticoagulation';
import TeeCertificationScreen from './components/screens/TeeCertificationScreen';
import UpdateBanner from './components/UpdateBanner';
import { onUpdateReady, applyUpdate, deferUpdate, isUpdateDeferred } from './utils/appUpdate';
import { useAppHistory } from './hooks/useAppHistory';
import { NavigationContext } from './hooks/NavigationContext';
import ClinicalDisclaimer, { CLINICAL_PAGES, hasAcceptedDisclaimer } from './components/shared/ClinicalDisclaimer';
import InstallPrompt from './components/InstallPrompt';
import SplashScreen from './components/splash/SplashScreen';
import { Toaster } from 'react-hot-toast';

// The admin panel is a few people's tool; it and its PDF thumbnailer load only when opened
const AdminLogin = lazy(() => import('./AdminLogin'));
const AdminPanel = lazy(() => import('./AdminPanel'));
const adminFallback = <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center text-sm text-gray-500">Opening the admin panel…</div>;

// The splash plays on a cold launch (a fresh browser session, e.g. after the PWA is swiped
// away), but not if it already played within the last hour. iOS can kill a backgrounded PWA
// on its own, and relaunching after that shouldn't replay the animation.
const SPLASH_COOLDOWN_MS = 60 * 60 * 1000;

const shouldPlaySplash = () => {
  try {
    if (sessionStorage.getItem('splashPlayed')) return false;
    const lastPlayed = Number(localStorage.getItem('splashLastPlayed')) || 0;
    return Date.now() - lastPlayed > SPLASH_COOLDOWN_MS;
  } catch {
    return false;
  }
};

const recordSplashPlayed = () => {
  try {
    sessionStorage.setItem('splashPlayed', 'true');
    localStorage.setItem('splashLastPlayed', String(Date.now()));
  } catch {
    // Storage unavailable (private mode, etc.); the splash simply plays next launch
  }
};

const App = () => {
  // Persist navigation state across app restarts
  const [currentPage, setCurrentPage] = useLocalStorage('currentPage', 'home');
  const [selectedHospital, setSelectedHospital] = useLocalStorage('selectedHospital', null);
  const [selectedTopic, setSelectedTopic] = useLocalStorage('selectedTopic', null);
  const [selectedCollection, setSelectedCollection] = useLocalStorage('selectedCollection', null);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProtocol, setSelectedProtocol] = useState(null);
  const [hospitalInitialAction, setHospitalInitialAction] = useState(null);
  const [hospitalsData, setHospitalsData] = useState([]);
  // Admin = signed in with Firebase Auth and carrying the `admin` custom claim
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAdmin, setShowAdmin] = useState(window.location.hash.startsWith('#admin'));
  // A setup link (#admin?code=...) shows the password screen even if someone else is signed in on this browser
  const [adminSetupPending, setAdminSetupPending] = useState(/^#admin\?.*code=/.test(window.location.hash));
  const [showUpdateBanner, setShowUpdateBanner] = useState(false);
  // Clinical Tools screens sit behind a one-time acknowledgement (see ClinicalDisclaimer)
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(hasAcceptedDisclaimer);
  const [updateReady, setUpdateReady] = useState(false); // a new build is waiting, banner shown or deferred
  // Splash plays on each cold launch (see shouldPlaySplash); onComplete records that it played
  const [showSplash, setShowSplash] = useState(shouldPlaySplash);
  // Set once the splash's flying laryngoscope has settled onto the header icon
  const [scopeLanded, setScopeLanded] = useState(false);

  // Swipe gesture state
  const [touchStartX, setTouchStartX] = useState(0);
  const [touchCurrentX, setTouchCurrentX] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // ---- navigation history --------------------------------------------------
  // Every screen is a history entry (see hooks/useAppHistory). `applyNav` is the only
  // place that sets page-level state; `go` pushes, `back` pops.
  const applyNav = (nav) => {
    setCurrentPage(nav.page);
    setSelectedHospital(nav.hospital ?? null);
    setHospitalInitialAction(nav.hospitalAction ?? null);
    setSelectedTopic(nav.topic ?? null);
    setSelectedCollection(nav.collection ?? null);
    setSelectedProtocol(nav.protocol ?? null);
    setSearchQuery(nav.query ?? '');
  };
  const navState = { page: currentPage, hospital: selectedHospital, hospitalAction: hospitalInitialAction, topic: selectedTopic, collection: selectedCollection, protocol: selectedProtocol, query: searchQuery };
  const { push: go, back, registerBackHandler } = useAppHistory(applyNav, navState);

  useEffect(() => {
  loadHospitals();
  // The splash lands its laryngoscope on the home header, so start there when it plays
  if (showSplash && currentPage !== 'home') setCurrentPage('home');
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

  // Follow the admin claim on the signed-in user (fires on sign-in, sign-out and token refresh)
  useEffect(() => startProfileSync(), []);
  useEffect(() => startDeviceSync(), []);
  useEffect(() => startNotifications(), []);
  useEffect(() => startAppBadge(), []);
  // A device that allowed notifications re-registers with the current profile (role, site) so audiences stay right
  const profileForPush = useProfile();
  useEffect(() => { refreshPushToken(profileForPush); }, [profileForPush]);
  useEffect(() => {
    let stop = () => {};
    onForegroundPush((m) => { const n = (m && m.notification) || {}; if (n.title) notify.info(n.title); }).then((s) => { stop = s || stop; });
    return () => stop();
  }, []);
  useEffect(() => onIdTokenChanged(auth, async (user) => {
    const claims = user ? (await user.getIdTokenResult()).claims : {};
    setIsAdmin(claims.admin === true);
  }), []);

  // New screens open at the top; coming back to Home returns to where you left it.
  // Home's position is tracked while scrolling (reading it on the way out is too late:
  // the next screen's first render can already have clamped it).
  const homeScrollRef = useRef(0);
  const rememberHomeScroll = () => { homeScrollRef.current = getScrollRoot()?.scrollTop || 0; };
  useEffect(() => {
    if (currentPage !== 'home') return undefined;
    const root = getScrollRoot();
    if (!root) return undefined;
    const onScroll = () => { homeScrollRef.current = root.scrollTop; };
    root.addEventListener('scroll', onScroll, { passive: true });
    return () => root.removeEventListener('scroll', onScroll);
  }, [currentPage]);
  const previousPageRef = useRef(currentPage);
  useEffect(() => {
    if (previousPageRef.current === currentPage) return;
    previousPageRef.current = currentPage;
    scrollRootTo(currentPage === 'home' ? homeScrollRef.current : 0);
  }, [currentPage]);

  // Track page views in analytics
  useEffect(() => {
    trackPageView(currentPage, {
      hospital_id: selectedHospital?.id,
      hospital_name: selectedHospital?.name,
      topic_name: selectedTopic?.name
    });
    logEvent('page', { page: currentPage, hospitalId: currentPage === 'hospital' ? selectedHospital?.id : null, topic: currentPage === 'education-topic' ? selectedTopic?.id : null });
  }, [currentPage, selectedHospital, selectedTopic]);

  // A new build is installed and waiting (see utils/appUpdate). Deferred with "Later" for the session.
  useEffect(() => onUpdateReady(() => {
    setUpdateReady(true);
    if (!isUpdateDeferred()) setShowUpdateBanner(true);
  }), []);

  const loadHospitals = async () => {
    try {
      // Add timeout for mobile Safari - Firestore connections can hang
      // If not loaded in 3 seconds, fall back to defaults
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Firestore timeout')), 3000)
      );

      const firestorePromise = getDocs(collection(db, 'hospitals'));

      const querySnapshot = await Promise.race([firestorePromise, timeoutPromise]);
      const hospitals = [];
      querySnapshot.forEach((doc) => {
        hospitals.push({ id: doc.id, ...doc.data() });
      });

      if (hospitals.length === 0) {
        setHospitalsData(getDefaultHospitals());
      } else {
        setHospitalsData(hospitals);
      }
    } catch (error) {
      console.log('Using default hospital data (timeout or error):', error.message);
      setHospitalsData(getDefaultHospitals());
    }
  };

  const getDefaultHospitals = () => [
    { id: 'ben-taub', name: 'Ben Taub General Hospital', color: 'from-[#00697f] to-[#004d5c]', shortName: 'Ben Taub' },
    { id: 'st-lukes', name: "Baylor St. Luke's Medical Center", color: 'from-[#2596be] to-[#1a7396]', shortName: "BSLMC Main OR" },
    { id: 'texas-heart', name: 'The Texas Heart Institute', color: 'from-[#9F8D70] to-[#8a7a61]', shortName: 'THI' },
    { id: 'texas-childrens', name: "Texas Children's Hospital", color: 'from-[#da2530] to-[#b81e28]', shortName: "TCH" },
    { id: 'va', name: 'Michael E. DeBakey VA Medical Center', color: 'from-[#1f4573] to-[#162E51]', shortName: 'MEDVAMC' },
    { id: 'md-anderson', name: 'MD Anderson Cancer Center', color: 'from-[#da322a] to-[#b82820]', shortName: 'MD Anderson' }
  ];

  const navigateToHospital = (hospital, action = null) => go({ page: 'hospital', hospital, hospitalAction: action });
  const navigateToSearch = (query) => go({ page: 'search', query });
  const navigateToEmergency = (protocol = null) => go({ page: 'emergency', protocol });
  const navigateToEducation = () => go({ page: 'education' });
  const navigateToEducationTopic = (topic) => go({ page: 'education-topic', topic });
  const navigateToCollection = (collection) => go({ page: 'education-collection', collection });
  const navigateToEducationBrowse = () => go({ page: 'education-browse' });
  const navigateToConcerns = () => go({ page: 'concerns' });
  const navigateToNeedlestick = () => go({ page: 'needlestick' });
  const navigateToBCMAnesthesiology = () => go({ page: 'bcm-anesthesiology' });
  const navigateToBCMResources = () => go({ page: 'bcm-resources' });
  const navigateToResearchHub = () => go({ page: 'research-hub' });
  const navigateToClinicalTools = () => go({ page: 'clinical-tools' });
  const navigateToMedicationReference = () => go({ page: 'medication-reference' });
  const navigateToPreopGuide = () => go({ page: 'preop-guide' });
  const navigateToASRACoags = () => go({ page: 'asra-coags' });
  const navigateToTeeCertification = () => go({ page: 'tee-certification' });

  // Handle navigation from search results
  const handleSearchNavigation = (type, data, action = null) => {
    if (type === 'emergency') { navigateToEmergency(data); return; }
    if (type === 'hospital') { navigateToHospital(data, action); return; }
    if (type === 'education') {
      const topic = data && topicByLabel(data.category);
      if (topic) {
        navigateToEducationTopic(topicNav(topic, { initialContentId: data.contentId, initialSubcategory: data.subcategory }));
      } else {
        navigateToEducation();
      }
      return;
    }
    const simple = ['needlestick', 'concerns', 'bcm-anesthesiology', 'bcm-resources', 'research-hub', 'clinical-tools', 'medication-reference', 'preop-guide', 'asra-coags', 'tee-certification'];
    if (simple.includes(type)) go({ page: type });
  };

  // Swipe gesture handlers
  const handleTouchStart = (e) => {
    // Only enable swipe from left edge and not on home screen
    if (currentPage === 'home' || e.touches[0].clientX > 30) return;
    setTouchStartX(e.touches[0].clientX);
    setTouchCurrentX(e.touches[0].clientX);
    setIsSwiping(true);
  };

  const handleTouchMove = (e) => {
    if (!isSwiping) return;
    setTouchCurrentX(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!isSwiping) return;

    const swipeDistance = touchCurrentX - touchStartX;
    const threshold = 100; // Minimum swipe distance to trigger navigation

    if (swipeDistance > threshold) {
      // Swipe right detected - trigger slide-out animation then go back
      setIsTransitioning(true);
      setTimeout(() => {
        back();
        setIsTransitioning(false);
      }, 300); // Match the CSS transition duration
    }

    // Reset swipe state
    setIsSwiping(false);
    setTouchStartX(0);
    setTouchCurrentX(0);
  };

  // Calculate swipe progress for visual feedback
  const swipeProgress = isSwiping ? Math.min((touchCurrentX - touchStartX) / 200, 1) : 0;

  // Handle update banner actions
  const handleUpdate = async () => {
    const applied = await applyUpdate();
    if (!applied) window.location.reload();
  };

  const handleDismissUpdate = () => {
    deferUpdate();
    setShowUpdateBanner(false);
  };

  return (
    <>
      {/* Toast notifications (see utils/notify.js); sits below the status bar on phones */}
      <Toaster
        position="top-center"
        containerStyle={{ top: 'max(1rem, env(safe-area-inset-top))' }}
        toastOptions={{
          duration: 3000,
          className: '!bg-white !text-gray-900 dark:!bg-gray-800 dark:!text-white !rounded-xl !shadow-lg !text-sm !font-medium !max-w-[92vw]',
        }}
      />

      {/* Home-screen install invitation (second visit onward, browser only) */}
      {!showSplash && currentPage === 'home' && <InstallPrompt />}

      {/* Splash Screen */}
      {showSplash && (
        <SplashScreen
          onScopeLanded={() => setScopeLanded(true)}
          onComplete={() => {
            setShowSplash(false);
            recordSplashPlayed();
          }}
        />
      )}

      {/* Main App */}
      <div
        className="min-h-screen bg-gray-50 dark:bg-gray-900 relative"
        // Splash covers this with z-index, no opacity transition needed
        // Swipe navigation RE-ENABLED - only swipe gestures, not hold-to-clear
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
    >
      {/* Update Banner */}
      {showUpdateBanner && (
        <UpdateBanner onUpdate={handleUpdate} onLater={handleDismissUpdate} />
      )}


      {/* Swipe indicator */}
      {isSwiping && swipeProgress > 0 && (
        <div
          className="fixed top-0 left-0 h-full w-1 bg-sky-500 transition-opacity"
          style={{
            opacity: swipeProgress,
            boxShadow: `0 0 ${20 * swipeProgress}px rgba(14, 165, 233, ${0.5 * swipeProgress})`
          }}
        />
      )}

      {!showAdmin && CLINICAL_PAGES.has(currentPage) && !disclaimerAccepted && (
        <ClinicalDisclaimer onAccept={() => setDisclaimerAccepted(true)} onDecline={back} />
      )}

      <NavigationContext.Provider value={{ back, registerBackHandler }}>
      {showAdmin ? (
        <Suspense fallback={adminFallback}>
          {isAdmin && !adminSetupPending ? <AdminPanel onLogout={() => setShowAdmin(false)} /> : <AdminLogin onSetupDone={() => setAdminSetupPending(false)} />}
        </Suspense>
      ) : (
        <>
          {currentPage === 'home' && (
            // Any tap on Home records its scroll position before navigation replaces the content
            <div onPointerDownCapture={rememberHomeScroll} onTouchStartCapture={rememberHomeScroll}>
            <HomeScreen
              hospitals={hospitalsData}
              updateReady={updateReady}
              onUpdateClick={() => setShowUpdateBanner(true)}
              onHospitalClick={navigateToHospital}
              onEmergencyClick={navigateToEmergency}
              hideLaryngoscopeIcon={showSplash && !scopeLanded}
              onEducationClick={navigateToEducation}
              onClinicalToolsClick={navigateToClinicalTools}
              onConcernsClick={navigateToConcerns}
              onNeedlestickClick={navigateToNeedlestick}
              onBCMAnesthesiologyClick={navigateToBCMAnesthesiology}
              onBCMResourcesClick={navigateToBCMResources}
              onResearchHubClick={navigateToResearchHub}
              onTeeCertificationClick={navigateToTeeCertification}
              onSearchClick={navigateToSearch}
            />
            </div>
          )}
          {currentPage !== 'home' && (
            <div key={currentPage} className={`page-enter transition-transform duration-300 ease-out ${isTransitioning ? 'translate-x-full' : 'translate-x-0'}`}>
              {currentPage === 'search' && (
                <SearchResultsScreen
                  onBack={back}
                  hospitals={hospitalsData}
                  initialQuery={searchQuery}
                  onNavigate={handleSearchNavigation}
                />
              )}
              {currentPage === 'hospital' && (
                <HospitalDetailScreen
                  hospital={selectedHospital}
                  onBack={back}
                  initialAction={hospitalInitialAction}
                />
              )}
              {currentPage === 'emergency' && (
                <EmergencyProtocolsScreen
                  onBack={back}
                  initialProtocol={selectedProtocol}
                />
              )}
              {currentPage === 'education' && (
                <EducationCenterScreen onBack={back} onTopicClick={navigateToEducationTopic} onCollectionClick={navigateToCollection} onBrowseClick={navigateToEducationBrowse} />
              )}
              {currentPage === 'education-topic' && selectedTopic && (
                <EducationTopicScreen topic={selectedTopic} onBack={back} onCollectionClick={navigateToCollection} />
              )}
              {currentPage === 'education-browse' && (
                <EducationBrowseScreen onBack={back} onTopicClick={navigateToEducationTopic} />
              )}
              {currentPage === 'education-collection' && selectedCollection && (
                <EducationCollectionScreen collection={selectedCollection} onBack={back} />
              )}
              {currentPage === 'concerns' && (
                <ConcernsScreen onBack={back} />
              )}
              {currentPage === 'needlestick' && (
                <NeedlestickScreen onBack={back} />
              )}
              {currentPage === 'bcm-anesthesiology' && (
                <BCMAnesthesiologyScreen onBack={back} onTeeCertificationClick={navigateToTeeCertification} />
              )}
              {currentPage === 'bcm-resources' && (
                <BCMResourcesScreen onBack={back} />
              )}
              {currentPage === 'research-hub' && (
                <ResearchHubScreen onBack={back} />
              )}
              {currentPage === 'clinical-tools' && (
                <ClinicalToolsScreen
                  onBack={back}
                  onMedicationReferenceClick={navigateToMedicationReference}
                  onPreopGuideClick={navigateToPreopGuide}
                  onASRACoagsClick={navigateToASRACoags}
                  onTeeCertificationClick={navigateToTeeCertification}
                />
              )}
              {['medication-reference', 'cv-medications', 'ob-medications', 'regional-medications', 'pediatric-medications'].includes(currentPage) && (
                <MedicationReferenceScreen
                  onBack={back}
                  initialCategory={{ 'cv-medications': 'cardiovascular', 'ob-medications': 'obstetric', 'regional-medications': 'regional', 'pediatric-medications': 'pediatric' }[currentPage] || 'cardiovascular'}
                />
              )}
              {currentPage === 'preop-guide' && (
                <PreopMedicationGuide onBack={back} />
              )}
              {currentPage === 'tee-certification' && (
                <TeeCertificationScreen onBack={back} />
              )}
              {currentPage === 'asra-coags' && (
                <ASRAAnticoagulation onBack={back} />
              )}
            </div>
          )}
        </>
      )}
      </NavigationContext.Provider>
      </div>
    </>
  );
};

export default App;
